import { Col, Row, Typography, theme } from "antd";
import { PropsWithChildren } from "react";
import { useApplicationContext } from "src/hooks/useApplication";
import { ProgressBar } from "src/components/ProgressBar";
import { attributeToTitle } from "src/utils/strings";
import { Time, TimeDuration } from "src/components/Time";
import { Section } from "./Section";
import { TagsList } from "./TagsList";
import { VzblTag } from "./VzblTag";
import { useEntityContext } from "../useEntityContext";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { columnInfo, Api } from "@vzbl/integrations";
import { asKey } from "src/utils/fields";

const { Link } = Typography;

type TypeRender =
  | "bar"
  | "string"
  | "date"
  | "durationInMillis"
  | "url"
  | "tag"
  | "tags";

const KeyValue = (props: {
  keyName: string;
  entity: any;
  type: TypeRender;
  link?: boolean;
}) => {
  const orgId = useApplicationContext().org_id;
  const context = useEntityContext();
  if (!orgId) {
    throw new Error("orgId is undefined");
  }
  const value = props.entity.properties[props.keyName];
  const shouldLink = props.link === undefined ? true : props.link;
  switch (props.type) {
    case "bar": {
      return <ProgressBar key={props.keyName} value={value || "0"} />;
    }
    case "date": {
      return <Time value={value} />;
    }
    case "durationInMillis": {
      return <TimeDuration key={props.keyName} value={value as number} />;
    }
    case "url": {
      return (
        <Link href={value as string} target="_blank">
          {value}
        </Link>
      );
    }
    case "tag": {
      return (
        <VzblTag
          orgId={orgId}
          context={context}
          text={value as string}
          color="none"
          key={props.keyName}
          linkParams={{
            orgId,
            attribute: props.keyName,
            entityType: props.entity.labels[0],
            value: value,
          }}
        />
      );
    }
    case "tags": {
      return (
        <TagsList
          tags={(value as string[]) || []}
          threshold={2}
          color="default"
          tag={(tag, index) => (
            <VzblTag
              orgId={orgId}
              context={context}
              text={tag}
              color="none"
              key={index}
              linkParams={{
                orgId,
                attribute: props.keyName,
                entityType: props.entity.labels[0],
                value: tag,
              }}
            />
          )}
        />
      );
    }
    default: {
      if (shouldLink) {
        return (
          <VzblTag
            orgId={orgId}
            context={context}
            text={value as string}
            color="none"
            linkParams={{
              orgId,
              attribute: props.keyName,
              entityType: props.entity.labels[0],
              value: value,
            }}
          />
        );
      }
      return <>{value}</>;
    }
  }
};

type SummaryItem = {
  key: string;
  type: TypeRender;
  label?: string;
  link?: boolean;
};
type SummaryMapType = {
  [key: string]: {
    vzbl?: {
      main?: SummaryItem[];
      left?: SummaryItem[];
      right?: SummaryItem[];
    };
    left: SummaryItem[];
    right: SummaryItem[];
  };
};
const filteredSummaryMap = (
  entityType: string,
  config: Api.Schema.IntegrationConfiguration
): SummaryMapType[string] => {
  const candidate = SummaryMap[entityType];
  if (candidate === undefined) {
    return candidate;
  }
  const keepItem = (item: SummaryItem) =>
    columnInfo(asKey({ target: entityType, attribute: item.key }), config) !=
    null;
  return {
    vzbl: {
      main: candidate.vzbl?.main?.filter(keepItem),
      left: candidate.vzbl?.left?.filter(keepItem),
      right: candidate.vzbl?.right?.filter(keepItem),
    },
    left: candidate.left.filter(keepItem),
    right: candidate.right.filter(keepItem),
  };
};

const SummaryMap: SummaryMapType = {
  Branch: {
    left: [
      { key: "repository", type: "string" },
      { key: "integration", type: "string" },
    ],
    right: [{ key: "status", type: "tag" }],
  },
  Channel: {
    left: [{ key: "name", type: "string" }],
    right: [{ key: "purpose", type: "string" }],
  },
  Commit: {
    vzbl: {
      main: [
        { key: "vzblSummary", type: "string", label: "Summary", link: false },
      ],
    },
    left: [
      { key: "totalFilesAdded", type: "string", link: false },
      { key: "totalFilesDeleted", type: "string", link: false },
      { key: "totalFilesModified", type: "string", link: false },
    ],
    right: [
      { key: "vzblMaintainability", type: "bar", label: "Maintainability" },
      { key: "vzblReadability", type: "bar", label: "Readability" },
      { key: "vzblComplexity", type: "bar", label: "Complexity" },
    ],
  },
  CicdPipeline: {
    left: [
      { key: "state", type: "tag" },
      { key: "createdAt", type: "date" },
    ],
    right: [
      { key: "branch", type: "string" },
      { key: "repository", type: "string" },
      { key: "integration", type: "string" },
    ],
  },
  Issue: {
    vzbl: {
      main: [
        {
          key: "summary",
          type: "string",
          label: "summary",
          link: false,
        },
      ],
    },
    left: [
      {
        key: "vzblIssueValueStatement",
        type: "string",
        label: "Value Statement",
      },
      { key: "vzblKeywords", type: "tags", label: "Keywords" },
      { key: "vzblTopics", type: "tags", label: "Topics" },
      { key: "vzblImpact", type: "string", label: "Impact" },
    ],
    right: [
      { key: "vzblUrgency", type: "string", label: "Urgency" },
      { key: "vzblImportance", type: "bar", label: "Importance" },
      { key: "statusChangeDate", type: "date", label: "Status Changed" },
      { key: "updatedAt", type: "date" },
      { key: "createdAt", type: "date" },
    ],
  },
  IssueComment: {
    left: [
      { key: "vzblCategory", type: "string", label: "Category" },
      { key: "vzblSentiment", type: "string", label: "Sentiment" },
      { key: "vzblEmotion", type: "string", label: "Emotion" },
    ],
    right: [
      { key: "vzblTone", type: "string", label: "Tone" },
      { key: "vzblKeywords", type: "tags", label: "Keywords" },
      { key: "vzblTopics", type: "tags", label: "Topics" },
    ],
  },
  Message: {
    left: [{ key: "name", type: "string", link: false }],
    right: [{ key: "createdAt", type: "date" }],
  },
  Person: {
    left: [{ key: "name", type: "string", link: false }],
    right: [{ key: "email", type: "string", link: false }],
  },
  PullRequest: {
    vzbl: {
      main: [
        { key: "vzblSummary", type: "string", label: "Summary", link: false },
      ],
    },
    left: [
      { key: "state", type: "tag" },
      { key: "status", type: "tag" },
      { key: "mergedAt", type: "date" },
      { key: "closedAt", type: "date" },
      { key: "createdAt", type: "date" },
      { key: "updatedAt", type: "date" },
    ],
    right: [
      { key: "additions", type: "string" },
      { key: "deletions", type: "string" },
      { key: "commits", type: "string" },
      { key: "vzblRiskScore", type: "bar", label: "Risk Score" },
      { key: "vzblUrgency", type: "bar", label: "Urgency" },
    ],
  },
  PullRequestReviewComment: {
    left: [
      { key: "vzblLanguage", type: "string", label: "Language" },
      { key: "vzblCategory", type: "string", label: "Category" },
      { key: "vzblEmotion", type: "string", label: "Emotion" },
    ],
    right: [
      { key: "vzblTone", type: "string", label: "Tone" },
      { key: "vzblKeywords", type: "tags", label: "Keywords" },
      { key: "vzblTopics", type: "tags", label: "Topics" },
    ],
  },
  Project: {
    left: [
      { key: "projectKey", type: "string" },
      { key: "name", type: "string" },
    ],
    right: [
      { key: "projectTypeKey", type: "string" },
      { key: "integration", type: "string" },
    ],
  },
  Release: {
    left: [
      { key: "releaseName", type: "string" },
      { key: "body", type: "string", link: false },
      { key: "tagName", type: "string" },
      { key: "status", type: "tag" },
    ],
    right: [
      { key: "draft", type: "string" },
      { key: "preRelease", type: "string" },
      { key: "createdAt", type: "date" },
    ],
  },
  Sprint: {
    left: [
      { key: "name", type: "string" },
      { key: "startDate", type: "date" },
      { key: "endDate", type: "date" },
    ],
    right: [
      { key: "state", type: "tag" },
      { key: "status", type: "tag" },
    ],
  },
};

const attributeColumn = (entity: any) => {
  return function renderColumn(item: SummaryItem, index: number) {
    return (
      <Row key={index} style={{ lineHeight: "24px" }}>
        <Col span={8} style={{ fontSize: 12 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {item.label || attributeToTitle(item.key)}
          </Typography.Text>
        </Col>
        <Col span={12} style={{ fontSize: 12 }}>
          <Typography.Text style={{ fontSize: 12 }}>
            <KeyValue
              key={item.key}
              keyName={item.key}
              entity={entity}
              type={item.type}
              link={item.link}
            />
          </Typography.Text>
        </Col>
      </Row>
    );
  };
};

export const ExpandedView = (
  props: PropsWithChildren<{
    style?: React.CSSProperties;
  }>
) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        marginLeft: "40px",
        borderLeft: `1px solid ${token.colorBorderSecondary}`,
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
};

export const EntitySummary = (props: { entity: any; showHeader?: boolean }) => {
  const config = useIntegrationConfig();
  const entityType = props.entity.labels[0];
  const candidateMap = filteredSummaryMap(entityType, config as any);
  return candidateMap ? (
    <Section style={{ paddingRight: 0, marginBottom: "10px" }}>
      {SummaryMap[props.entity.labels[0]].vzbl && (
        <Row>
          {SummaryMap[props.entity.labels[0]].vzbl?.main?.map((item, index) => {
            return (
              <Row style={{ marginBottom: "10px" }} key={index}>
                <Col key={item.key} span={24} className="entity">
                  <div>
                    <Typography.Text type="secondary" className="entity">
                      {item.label || attributeToTitle(item.key)}
                    </Typography.Text>
                  </div>
                  <div>
                    <KeyValue
                      keyName={item.key}
                      entity={props.entity}
                      type={item.type}
                      link={item.link}
                    />
                  </div>
                </Col>
              </Row>
            );
          })}
          <Row style={{ width: "100%" }}>
            <Col span={12} style={{ lineHeight: "20px" }}>
              {SummaryMap[props.entity.labels[0]].vzbl?.left?.map(
                attributeColumn(props.entity)
              )}
            </Col>
            <Col span={12} style={{ lineHeight: "20px" }}>
              {SummaryMap[props.entity.labels[0]].vzbl?.right?.map(
                attributeColumn(props.entity)
              )}
            </Col>
          </Row>
        </Row>
      )}
      <Row>
        <Col span={12} style={{ lineHeight: "20px" }}>
          {SummaryMap[props.entity.labels[0]].left.map(
            attributeColumn(props.entity)
          )}
        </Col>
        <Col span={12} style={{ lineHeight: "20px" }}>
          {SummaryMap[props.entity.labels[0]].right.map(
            attributeColumn(props.entity)
          )}
        </Col>
      </Row>
    </Section>
  ) : null;
};
